import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { getSupabase, withTimeout } from "@/lib/hooks/api/utils";
import { Tickets } from "@/src/gql/graphql";

export function useDeleteTicketWithNavigation() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async ({ ticketId, ticketNumber }: { ticketId: string; ticketNumber: string }) => {
      if (
        !window.confirm(
          `Are you sure you want to delete ticket ${ticketNumber}? This action cannot be undone.`
        )
      ) {
        throw new Error("Deletion cancelled by user");
      }

      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").delete().eq("id", ticketId).select().single(),
        15000,
        "delete ticket"
      );

      if (error) {
        console.error("Error deleting ticket:", error);
        throw new Error(error.message);
      }

      return data as Tickets;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket deleted successfully");
      router.push("/tickets");
    },
    onError: (error) => {
      if (error.message !== "Deletion cancelled by user") {
        toast.error("Failed to delete ticket");
        console.error("Error deleting ticket:", error);
      }
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ticketNumber }: { id: string; ticketNumber: string }) => {
      if (
        !window.confirm(
          `Are you sure you want to delete ticket ${ticketNumber}? This action cannot be undone.`
        )
      ) {
        throw new Error("Deletion cancelled by user");
      }

      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").delete().eq("id", id).select().single(),
        15000,
        "delete ticket"
      );

      if (error) {
        console.error("Error deleting ticket:", error);
        throw new Error(error.message);
      }

      return data as Tickets;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void queryClient.invalidateQueries({ queryKey: ["tags", "tickets"] });
      fetch("/api/tags/invalidate/tickets", { method: "POST" }).catch((error) => {
        console.error("Failed to invalidate server-side ticket tags cache:", error);
      });

      toast.success("Ticket deleted successfully");
    },
    onError: (error) => {
      if (error.message !== "Deletion cancelled by user") {
        toast.error("Failed to delete ticket");
        console.error("Error deleting ticket:", error);
      }
    },
  });
}
